import React, { useEffect, useMemo, useState } from "react";
import {
  FLEET_FIRST_MACHINE_NO,
  FLEET_LAST_MACHINE_NO,
  FLEET_TARGET_MACHINES
} from "../constants/fleet";
import API from "../services/api";
import { formatDateTimeDMY } from "../utils/dateFormat";

const ALERT_LABELS = {
  silent: "Sin señal",
  usb_down: "USB reportado off",
  version_stale: "App desactualizada",
  parse_error: "Error parseo VEND"
};

const OFFLINE_MS = 2 * 60 * 60 * 1000;
const APP_UPDATE_FRESH_MS = 20 * 60 * 1000;
const JUST_UPDATED_MS = 30 * 60 * 1000;
const STALL_AFTER_ACK_MS = 15 * 60 * 1000;
const WIFI_SLOTS = 4;

function emptyWifiRows() {
  return Array.from({ length: WIFI_SLOTS }, () => ({ ssid: "", psk: "" }));
}

function lastSeenMs(d) {
  const t = d.last_seen_at || d.qibixx_last_report_at;
  return t ? new Date(t).getTime() : 0;
}

function isSilent(d) {
  const last = lastSeenMs(d);
  return !last || Date.now() - last > OFFLINE_MS;
}

function deriveUpdateProgress(d, serverApk) {
  const targetName = serverApk?.version_name ? String(serverApk.version_name).trim() : "";
  const current = d.reported_app_version ? String(d.reported_app_version).trim() : "";
  const onTarget = Boolean(targetName && current && current === targetName);
  const cmd = d.update_command;
  const au = d.app_update && typeof d.app_update === "object" ? d.app_update : null;
  const auAt = au?.at ? new Date(au.at).getTime() : 0;
  const auFresh = auAt && Date.now() - auAt < APP_UPDATE_FRESH_MS;

  if (onTarget) {
    const ackAt = cmd?.acked_at ? new Date(cmd.acked_at).getTime() : 0;
    const createdAt = cmd?.created_at ? new Date(cmd.created_at).getTime() : 0;
    const justUpdated =
      (ackAt && Date.now() - ackAt < JUST_UPDATED_MS) ||
      (createdAt && Date.now() - createdAt < JUST_UPDATED_MS);
    if (!justUpdated) return null;
    return {
      kind: "done",
      pct: 100,
      indeterminate: false,
      highlight: true,
      message: `Nueva versión cargada: ${current}`
    };
  }

  if (auFresh && au.phase) {
    const pct =
      typeof au.pct === "number" && Number.isFinite(au.pct)
        ? Math.max(0, Math.min(100, au.pct))
        : au.phase === "failed"
          ? 100
          : 45;
    if (au.phase === "failed") {
      return {
        kind: "failed",
        pct: 100,
        indeterminate: false,
        message: au.message || "Falló la actualización en el Moto"
      };
    }
    if (au.phase === "installed" || au.phase === "done") {
      return {
        kind: "progress",
        pct: 90,
        indeterminate: true,
        message: au.message || "Instalada. Esperando que el Moto reporte la versión…"
      };
    }
    return {
      kind: "progress",
      pct,
      indeterminate: pct < 8 || au.phase === "queued" || au.phase === "checking",
      message: au.message || `Actualizando (${au.phase})…`
    };
  }

  if (!cmd) {
    if (targetName && current && current !== targetName) {
      return {
        kind: "stale",
        pct: 0,
        indeterminate: false,
        message: `${current} · servidor ${targetName}`
      };
    }
    return null;
  }

  if (cmd.status === "failed") {
    return {
      kind: "failed",
      pct: 100,
      indeterminate: false,
      message: cmd.result || "El Moto rechazó la actualización"
    };
  }

  if (cmd.status === "pending") {
    return {
      kind: "waiting",
      pct: 12,
      indeterminate: true,
      message: isSilent(d)
        ? "Esperando señal del Moto para descargar…"
        : "Comando en cola. El Moto lo toma en unos segundos…"
    };
  }

  if (cmd.status === "dispatched") {
    return {
      kind: "progress",
      pct: 40,
      indeterminate: true,
      message: "El Moto recibió el pedido. Descargando…"
    };
  }

  if (cmd.status === "acked") {
    const ackedAt = cmd.acked_at ? new Date(cmd.acked_at).getTime() : 0;
    if (ackedAt && Date.now() - ackedAt > STALL_AFTER_ACK_MS) {
      return {
        kind: "stalled",
        pct: 70,
        indeterminate: false,
        message:
          "El Moto aceptó el pedido pero aún no reporta la nueva versión. Puede faltar confirmar en pantalla o se cortó la red."
      };
    }
    return {
      kind: "progress",
      pct: 65,
      indeterminate: true,
      message: "Descargando e instalando en el Moto…"
    };
  }

  return null;
}

function UpdateBar({ progress }) {
  if (!progress) return null;
  const showBar = progress.kind !== "stale";
  return (
    <div className={`fleet-update fleet-update--${progress.kind}`}>
      {showBar ? (
        <div
          className="fleet-update__track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.indeterminate ? undefined : progress.pct}
        >
          <div
            className={
              "fleet-update__fill" +
              (progress.indeterminate ? " fleet-update__fill--pulse" : "")
            }
            style={{ width: `${Math.max(8, progress.pct || 0)}%` }}
          />
        </div>
      ) : null}
      <p className="fleet-update__msg">{progress.message}</p>
    </div>
  );
}

function Fleet() {
  const [data, setData] = useState(null);
  const [minVersion, setMinVersion] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingMin, setSavingMin] = useState(false);
  const [serverApk, setServerApk] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [fleetUpdating, setFleetUpdating] = useState(false);
  const [fleetJob, setFleetJob] = useState(null);
  const [wifiRows, setWifiRows] = useState(emptyWifiRows);
  const [wifiFallback, setWifiFallback] = useState(true);
  const [wifiLoaded, setWifiLoaded] = useState(false);
  const [savingWifi, setSavingWifi] = useState(false);

  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await API.get("/devices/fleet");
      setData(res.data);
      setMinVersion(res.data.fleet_min_app_version || "");
    } catch (e) {
      if (!quiet) setData(null);
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    API.get("/releases/info")
      .then(res => setServerApk(res.data))
      .catch(() => setServerApk(null));
  }, []);

  const terminals = useMemo(
    () => (data?.terminals || []).filter(t => t.active !== false),
    [data]
  );

  useEffect(() => {
    if (!data || wifiLoaded) return;
    const src = (data.terminals || []).find(t => t.active !== false) || data.terminals?.[0];
    const nets = src?.remote_config_effective?.wifi_trusted_networks;
    if (Array.isArray(nets) && nets.length) {
      const rows = emptyWifiRows();
      nets.slice(0, WIFI_SLOTS).forEach((n, i) => {
        rows[i] = { ssid: n.ssid || "", psk: "" };
      });
      setWifiRows(rows);
    }
    setWifiFallback(src?.remote_config_effective?.wifi_cellular_fallback !== false);
    setWifiLoaded(true);
  }, [data, wifiLoaded]);

  const progresses = useMemo(
    () =>
      terminals.map(d => ({
        id: d.id,
        progress: deriveUpdateProgress(d, serverApk)
      })),
    [terminals, serverApk]
  );

  const inFlight = progresses.some(
    p => p.progress && (p.progress.kind === "progress" || p.progress.kind === "waiting")
  );

  const rollup = useMemo(() => {
    let waiting = 0;
    let downloading = 0;
    let done = 0;
    let failed = 0;
    let stale = 0;
    progresses.forEach(({ progress }) => {
      if (!progress) return;
      if (progress.kind === "waiting") waiting += 1;
      else if (progress.kind === "progress") downloading += 1;
      else if (progress.kind === "done" && progress.highlight) done += 1;
      else if (progress.kind === "failed" || progress.kind === "stalled") failed += 1;
      else if (progress.kind === "stale") stale += 1;
    });
    const active = waiting + downloading;
    const pct =
      fleetUpdating
        ? 18
        : active + done + failed === 0
          ? 0
          : Math.round(((done + downloading * 0.55 + waiting * 0.15) / Math.max(1, active + done + failed)) * 100);
    return { waiting, downloading, done, failed, stale, active, pct };
  }, [progresses, fleetUpdating]);

  useEffect(() => {
    const ms = inFlight || fleetUpdating ? 4000 : 45000;
    const t = setInterval(() => load(true), ms);
    return () => clearInterval(t);
  }, [inFlight, fleetUpdating]);

  const saveMinVersion = async () => {
    setSavingMin(true);
    try {
      await API.put("/devices/fleet-min-version", {
        min_app_version: minVersion.trim() || null
      });
      await load(true);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo guardar");
    } finally {
      setSavingMin(false);
    }
  };

  const enqueueReopenUsb = async id => {
    try {
      await API.post(`/devices/${id}/commands`, { command_type: "REOPEN_USB" });
      await load(true);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo encolar comando");
    }
  };

  const enqueueUpdateApp = async id => {
    setUpdatingId(id);
    try {
      await API.post(`/devices/${id}/commands`, { command_type: "UPDATE_APP" });
      await load(true);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo encolar la actualización");
    } finally {
      setUpdatingId(null);
    }
  };

  const enqueueForegroundApp = async id => {
    try {
      await API.post(`/devices/${id}/commands`, { command_type: "FOREGROUND_APP" });
      await load(true);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo traer Vikingo al frente");
    }
  };

  const enqueueSumupLogin = async id => {
    try {
      await API.post(`/devices/${id}/commands`, { command_type: "SUMUP_LOGIN" });
      await load(true);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo abrir el login SumUp");
    }
  };

  const enqueueFleetUpdate = async () => {
    const target = serverApk?.version_name || "la APK del servidor";
    const outdated = terminals.filter(d => {
      const current = d.reported_app_version ? String(d.reported_app_version).trim() : "";
      return !current || current !== String(target).trim();
    });
    if (
      !window.confirm(
        `¿Actualizar la flota a ${target}?\n\n` +
          `Se encola de a una: ${outdated.length} terminal(es) activo(s) que aún no tienen esa versión. ` +
          `Los que están sin señal la bajan cuando vuelvan. ` +
          `Si varias están en línea, pueden coincidir en la descarga.`
      )
    ) {
      return;
    }
    setFleetUpdating(true);
    setFleetJob({ phase: "enqueueing", message: "Encolando actualizaciones una por una…" });
    try {
      const res = await API.post("/devices/fleet/update-app");
      setMinVersion(res.data.target_version || minVersion);
      setFleetJob({
        phase: "watching",
        target: res.data.target_version,
        queued: res.data.queued?.length || 0,
        skipped: res.data.skipped?.length || 0,
        message: `Encoladas ${res.data.queued?.length || 0}. Las que ya estaban al día se omitieron (${res.data.skipped?.length || 0}).`
      });
      await load(true);
    } catch (e) {
      setFleetJob(null);
      alert(e?.response?.data?.error || "No se pudo actualizar la flota");
    } finally {
      setFleetUpdating(false);
    }
  };

  const saveWifiNetworks = async () => {
    const saved = new Set(
      (terminals[0]?.remote_config_effective?.wifi_trusted_networks || [])
        .filter(n => n.psk_set)
        .map(n => n.ssid)
    );
    const networks = wifiRows
      .map(r => ({ ssid: (r.ssid || "").trim(), psk: (r.psk || "").trim() }))
      .filter(r => r.ssid);
    for (const n of networks) {
      if (!n.psk && !saved.has(n.ssid)) {
        alert(`Falta la clave de "${n.ssid}" (mínimo 8 caracteres, WPA2).`);
        return;
      }
    }
    setSavingWifi(true);
    try {
      await API.put("/devices/fleet/wifi-networks", {
        networks,
        wifi_cellular_fallback: wifiFallback
      });
      setWifiRows(rows => rows.map(r => ({ ssid: r.ssid, psk: "" })));
      setWifiLoaded(false);
      await load(true);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudieron guardar las redes Wi‑Fi");
    } finally {
      setSavingWifi(false);
    }
  };

  const toggleRemoteUsbDiag = async (d, value) => {
    try {
      await API.put(`/devices/${d.id}`, {
        name: d.name,
        machine_id: d.machine_id,
        active: d.active,
        qibixx_registration_id: d.qibixx_registration_id ?? null,
        remote_config: { usb_diagnostic_log: value }
      });
      await load(true);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo actualizar config");
    }
  };

  const toggleRemoteConfigFlag = async (d, key, value) => {
    try {
      await API.put(`/devices/${d.id}`, {
        name: d.name,
        machine_id: d.machine_id,
        active: d.active,
        qibixx_registration_id: d.qibixx_registration_id ?? null,
        remote_config: { [key]: value }
      });
      await load(true);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo actualizar config");
    }
  };

  if (loading && !data) {
    return (
      <div className="page-header">
        <h1 className="page-title">Flota</h1>
        <p>Cargando…</p>
      </div>
    );
  }

  const showRollup =
    fleetUpdating ||
    fleetJob ||
    rollup.active > 0 ||
    rollup.done > 0 ||
    rollup.failed > 0;

  const rollupMessage = fleetUpdating
    ? "Encolando actualizaciones una por una…"
    : fleetJob?.message && rollup.active === 0 && rollup.done > 0 && rollup.failed === 0
      ? `Flota actualizada a ${fleetJob.target || serverApk?.version_name}.`
      : rollup.active > 0
        ? `${rollup.waiting} esperando señal · ${rollup.downloading} descargando/instalando · ${rollup.done} listas`
        : fleetJob?.message || null;

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Flota terminales</h1>
        <p className="page-subtitle">
          Monitor para ~{FLEET_TARGET_MACHINES} equipos (N° máquina{" "}
          {`${FLEET_FIRST_MACHINE_NO}–${FLEET_LAST_MACHINE_NO}`}; Qibixx + Moto + SumUp + hub):
          última señal, versión de app,
          USB, alertas y acciones remotas. Umbral &quot;sin señal&quot;:{" "}
          <strong>{data?.offline_threshold_hours ?? "—"} h</strong> sin{" "}
          <code>last_seen</code> ni reporte Qibixx.
        </p>
      </header>

      <div className="card card--padded" style={{ marginBottom: "1.25rem" }}>
        <h2 className="section-title">Versión mínima esperada (apk)</h2>
        <p style={{ fontSize: "0.9rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>
          Si el terminal reporta otra versión vía telemetría, aparece alerta &quot;App
          desactualizada&quot;. Comparación exacta de string (ej.{" "}
          <code>7.0.9-vikingo19</code>).
        </p>
        {serverApk?.available ? (
          <p style={{ fontSize: "0.9rem", marginBottom: "0.75rem" }}>
            APK en el servidor: <strong>{serverApk.version_name}</strong>
            {serverApk.version_code != null ? ` (code ${serverApk.version_code})` : ""}. El Moto la
            descarga del droplet por internet; no hace falta el Mac.
          </p>
        ) : null}
        <div className="field-row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
          <input
            className="input"
            placeholder="ej. 7.0.0-vikingo6"
            value={minVersion}
            onChange={e => setMinVersion(e.target.value)}
            style={{ minWidth: "220px" }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={saveMinVersion}
            disabled={savingMin}
          >
            {savingMin ? "Guardando…" : "Guardar"}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={enqueueFleetUpdate}
            disabled={fleetUpdating || !serverApk?.available || terminals.length === 0}
          >
            {fleetUpdating ? "Encolando flota…" : "Actualizar toda la flota"}
          </button>
        </div>
        <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: 0 }}>
          Actualizar toda la flota encola la APK publicada de a un terminal. Cada Moto la baja
          cuando Vikingo está abierto y tiene red.
        </p>
      </div>

      <div className="card card--padded" style={{ marginBottom: "1.25rem" }}>
        <h2 className="section-title">Wi‑Fi de confianza y datos móviles</h2>
        <p style={{ fontSize: "0.9rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>
          El Moto (app 26+) se une primero a estas redes WPA2. Si esa Wi‑Fi no llega a Vikingo 2,
          usa el chip con plan de datos. Dejá Wi‑Fi y datos móviles encendidos en Android. La
          primera vez el teléfono puede pedir aceptar la red sugerida. Clave vacía = no cambiar
          la ya guardada.
        </p>
        {wifiRows.map((row, i) => {
          const saved = (terminals[0]?.remote_config_effective?.wifi_trusted_networks || []).some(
            n => n.ssid === row.ssid && n.psk_set
          );
          return (
            <div
              key={i}
              className="field-row"
              style={{ flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.5rem" }}
            >
              <input
                className="input"
                placeholder={`SSID ${i + 1}`}
                value={row.ssid}
                onChange={e => {
                  const v = e.target.value;
                  setWifiRows(rows => rows.map((r, j) => (j === i ? { ...r, ssid: v } : r)));
                }}
                style={{ minWidth: "180px", flex: "1 1 180px" }}
                autoComplete="off"
              />
              <input
                className="input"
                type="password"
                placeholder={saved ? "Clave guardada · escribir para cambiar" : "Clave WPA2 (mín. 8)"}
                value={row.psk}
                onChange={e => {
                  const v = e.target.value;
                  setWifiRows(rows => rows.map((r, j) => (j === i ? { ...r, psk: v } : r)));
                }}
                style={{ minWidth: "220px", flex: "1 1 220px" }}
                autoComplete="new-password"
              />
            </div>
          );
        })}
        <label style={{ fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.4rem", margin: "0.5rem 0 0.75rem" }}>
          <input
            type="checkbox"
            checked={wifiFallback}
            onChange={e => setWifiFallback(e.target.checked)}
          />
          Si falla el Wi‑Fi, usar datos móviles
        </label>
        <button
          type="button"
          className="btn btn-primary"
          onClick={saveWifiNetworks}
          disabled={savingWifi || terminals.length === 0}
        >
          {savingWifi ? "Guardando…" : "Guardar redes en la flota"}
        </button>
      </div>

      {showRollup ? (
        <div className="fleet-roll card card--padded">
          <div
            className="fleet-update__track fleet-update__track--lg"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={fleetUpdating ? undefined : rollup.pct}
          >
            <div
              className={
                "fleet-update__fill" +
                (fleetUpdating || rollup.active > 0 ? " fleet-update__fill--pulse" : "") +
                (rollup.active === 0 && rollup.done > 0 && rollup.failed === 0
                  ? " fleet-update__fill--done"
                  : "")
              }
              style={{
                width: `${Math.max(8, fleetUpdating ? 22 : rollup.pct || 8)}%`
              }}
            />
          </div>
          <p className="fleet-roll__msg">
            {rollupMessage ||
              (rollup.failed
                ? `${rollup.failed} con problema. Revisá la fila.`
                : "Seguimiento de actualización")}
          </p>
        </div>
      ) : null}

      <div className="table-wrap">
        <table className="table-modern">
          <thead>
            <tr>
              <th>Terminal</th>
              <th>Máquina</th>
              <th>Última señal</th>
              <th>Versión app</th>
              <th>Modelo</th>
              <th>USB</th>
              <th>Alertas</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {terminals.length === 0 ? (
              <tr>
                <td colSpan={8}>Sin terminales.</td>
              </tr>
            ) : (
              terminals.map(d => {
                const progress = deriveUpdateProgress(d, serverApk);
                const busy = updatingId === d.id;
                const alreadyCurrent =
                  serverApk?.version_name &&
                  d.reported_app_version &&
                  String(d.reported_app_version).trim() === String(serverApk.version_name).trim();
                return (
                  <tr key={d.id}>
                    <td>
                      <strong>{d.name}</strong>
                      <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                        #{d.id}
                      </div>
                    </td>
                    <td>{d.machine_name || "—"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.85rem" }}>
                      {d.last_seen_at
                        ? formatDateTimeDMY(d.last_seen_at)
                        : d.qibixx_last_report_at
                          ? formatDateTimeDMY(d.qibixx_last_report_at)
                          : "—"}
                    </td>
                    <td>{d.reported_app_version || "—"}</td>
                    <td style={{ fontSize: "0.8rem", maxWidth: "120px", wordBreak: "break-word" }}>
                      {d.reported_device_model || "—"}
                    </td>
                    <td>{d.qibixx_usb_connected == null ? "—" : d.qibixx_usb_connected ? "Sí" : "No"}</td>
                    <td>
                      {(d.alerts || []).length === 0 ? (
                        <span style={{ color: "var(--color-text-muted)" }}>OK</span>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                          {d.alerts.map(a => (
                            <span
                              key={a}
                              className="badge"
                              style={{
                                fontSize: "0.7rem",
                                padding: "0.2rem 0.45rem",
                                borderRadius: "4px",
                                background:
                                  a === "silent" ? "rgba(200,80,80,0.2)" : "rgba(200,150,50,0.2)"
                              }}
                            >
                              {ALERT_LABELS[a] || a}
                            </span>
                          ))}
                        </div>
                      )}
                      {d.last_parse_error ? (
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "var(--color-danger, #a44)",
                            marginTop: "0.25rem",
                            maxWidth: "220px",
                            wordBreak: "break-word"
                          }}
                          title={d.last_parse_error}
                        >
                          {d.last_parse_error.slice(0, 80)}
                          {d.last_parse_error.length > 80 ? "…" : ""}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: "0.8rem" }}
                          onClick={() => enqueueReopenUsb(d.id)}
                        >
                          Reabrir USB
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ fontSize: "0.8rem" }}
                          onClick={() => enqueueUpdateApp(d.id)}
                          disabled={busy || alreadyCurrent || progress?.kind === "waiting" || progress?.kind === "progress"}
                        >
                          {busy
                            ? "Encolando…"
                            : alreadyCurrent
                              ? "Al día"
                              : progress?.kind === "waiting" || progress?.kind === "progress"
                                ? "Actualizando…"
                                : "Actualizar app"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: "0.8rem" }}
                          onClick={() => enqueueForegroundApp(d.id)}
                        >
                          Traer al frente
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: "0.8rem" }}
                          onClick={() => enqueueSumupLogin(d.id)}
                        >
                          Login SumUp
                        </button>
                        <UpdateBar progress={progress} />
                        <label style={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(d.remote_config_effective?.usb_diagnostic_log)}
                            onChange={e => toggleRemoteUsbDiag(d, e.target.checked)}
                          />
                          Diag. USB remoto
                        </label>
                        <label style={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(d.remote_config_effective?.hide_debug_ui)}
                            onChange={e => toggleRemoteConfigFlag(d, "hide_debug_ui", e.target.checked)}
                          />
                          Modo quiosco (ocultar demo)
                        </label>
                        <label style={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(d.remote_config_effective?.kiosk_mode)}
                            onChange={e => toggleRemoteConfigFlag(d, "kiosk_mode", e.target.checked)}
                          />
                          Pantalla completa
                        </label>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Fleet;
